
import { type AppRoute, type PerspectiveAppRoutes } from "./routeConfig";
import { hasPermission, hasAnyPermission } from "../utils/permissions";
import { devFeaturesEnabled, isDevOnlyRoute, isSuperAdminOnlyRoute } from "./devOnlyRoutes";
import { isModuleLicensed } from "../utils/license";

export type TreeNode = {
  id: string;
  name: string;
  labelKey: string;
  iconCss?: string;
  url?: string;
  hasChild?: boolean;
  pid?: string;
};

/**
 * Mapping from nodeId to entity name for permission checking.
 * This maps menu items to their corresponding entity permissions (e.g., "material:view").
 */
const nodeIdToEntityMap: Record<string, string | null> = {
  // Order Data
  // "2.1" Production Orders is SuperAdmin-only (see superAdminOnlyNodeIds); no permission gate.
  "2.2": "demand", // Demand
  "2.3": "productionschedule:optimize", // Production Order Optimization
  
  // Material Data
  "3.1": "material", // Products
  "3.1.5": "material", // Intermediate Products
  "3.2": "material", // Raw Materials
  "3.3": "stocklevel", // Stocks
  "3.4": "delivery", // Deliveries
  
  // Resource Data
  "4.1": "resourcetype", // Resource Types
  "4.2": "resource", // Resources
  "4.3": "maintenancewindow", // Maintenance Windows
  "4.4": "shifttemplate", // Shift Model Templates
  
  // Routing Data
  "5.1": "routing-product", // Product Routings
  "5.1.5": "routing-intermediate", // Intermediate Routings
  "5.2": "setuptime", // Setup Times
  "5.3": "setuptime", // Oven Setup Times
  "5.4": "setuptime", // Processing Line Setup Times
  
  // Settings
  "6.1.1": "system:settings", // System Settings
  "6.1.2": "user", // User Management
  "6.1.3": "role", // User Roles
  "6.1.4": "holiday", // Holidays
  "6.1.5": "data:export|data:import", // Data Management
  "6.1.6": "gantt-profile", // Gantt Visualization Profiles
  "6.1.7": "system:settings", // CSV Import Config
  "6.1.8": "system:settings", // Excel Export Config
  "6.1.9": "system:settings", // Licence
  "6.1.10": "demo:tenant:reset", // Demo areas (Prod demo server only)
  "6.3.1": null, // Debug Test Functions (no specific permission)
  "6.3.2": null, // Browser Info (no specific permission)
  "6.3.3": "data:backup|data:restore", // Backup Restore
  
  // Permissions
  "7": "permission", // Permissions
  
  // Scheduling
  "8": "scheduling", // Shift Plan
  "10": "productionschedule", // Production Schedules
  
  // Planning
  "11": "planning", // Production Plan Visualization
  "12": "campaignsequence", // Campaign Sequences
};

const DEMO_TENANTS_NODE_ID = "6.1.10";

const resolveRoutePermissions = (entityName: string): string[] => {
  return entityName.split('|').map((name) => {
    const segments = name.split(':');
    if (segments.length >= 3) {
      return name;
    }
    if (segments.length === 2) {
      return `${name}:view`;
    }
    return `${name}:view`;
  });
};

export function buildTreeForPerspective(
  perspective: string,
  userProfile: { 
    role: string; 
    permissions: { viewId?: string; read?: boolean; write?: boolean; action?: string }[]; 
    superAdmin?: boolean;
    licensedModules?: string[];
    demoServer?: boolean;
  } | null,
  perspectiveRoutes: PerspectiveAppRoutes[]
): TreeNode[] {
  const treeNodes: TreeNode[] = [];
  
  // Find the routes for the requested perspective
  const perspectiveRoute = perspectiveRoutes.find(pr => pr.perspective === perspective);
  if (!perspectiveRoute) return [];

  const walk = (routes: AppRoute[], parentPath: string, parentId?: string): boolean => {
    let hasAnyVisibleChild = false;
    
    for (const route of routes) {
      if (isDevOnlyRoute(route.nodeId) && !devFeaturesEnabled) {
        continue;
      }
      const isSuperAdmin = userProfile?.superAdmin || userProfile?.role === 'superadmin';
      if (isSuperAdminOnlyRoute(route.nodeId) && !isSuperAdmin) {
        continue;
      }
      // Pages of a module the planning area has no licence for are not offered, not even to
      // super admins: the backend answers 404 for them anyway.
      if (!isModuleLicensed(userProfile, route.module)) {
        continue;
      }
      // The demo areas page exists only on the Prod demo server; elsewhere the backend has no
      // endpoint for it, so it is not offered even to super admins.
      if (route.nodeId === DEMO_TENANTS_NODE_ID && !userProfile?.demoServer) {
        continue;
      }
      // Check access based on permissions
      let hasAccess = false;
      
      if (isSuperAdmin) {
        // Superadmin has access to everything
        hasAccess = true;
      } else {
        // Check if route has an entity mapping
        const entityName = nodeIdToEntityMap[route.nodeId];
        
        if (entityName) {
          const permissionsToCheck = resolveRoutePermissions(entityName);
          if (permissionsToCheck.length === 1) {
            hasAccess = hasPermission(permissionsToCheck[0], userProfile);
          } else {
            hasAccess = hasAnyPermission(permissionsToCheck, userProfile);
          }
        } else {
          // For routes without entity mapping, use old permission check (viewId) or admin role
          hasAccess = 
            userProfile?.permissions.some(p => p.viewId === route.nodeId && p.read) ||
            // Allow access to shift plan for admin users
            (route.nodeId === "8" && userProfile?.role === 'admin');
        }
      }

      // For folders (routes with children), check if any child is visible
      if (route.children && route.children.length > 0) {
        const childPath = parentPath + "/" + route.path;
        const hasVisibleChildren = walk(route.children, childPath, route.nodeId);
        
        // Only show folder if it has visible children
        if (!hasVisibleChildren) continue;
        hasAnyVisibleChild = true;
        
        // Add the folder node
        treeNodes.push({
          pid: parentId,
          id: route.nodeId,
          name: route.labelKey,
          labelKey: route.labelKey,
          iconCss: route.icon,
          url: childPath, // Folders can have URLs too (for navigation to folder pages)
          hasChild: true
        });
      } else if (hasAccess) {
        // Leaf node (actual page) - only add if user has access
        const currentPath = parentPath + "/" + route.path;
        treeNodes.push({
          pid: parentId,
          id: route.nodeId,
          name: route.labelKey, // Don't translate here - let the sidebar handle it
          labelKey: route.labelKey,
          iconCss: route.icon,
          url: currentPath, // used for navigation 
          hasChild: false
        });
        
        hasAnyVisibleChild = true;
      }
    }
    
    return hasAnyVisibleChild;
  };

  walk(perspectiveRoute.appRoutes, perspective);
  return treeNodes;
}

export function findNodeById(tree: TreeNode[], id: string): TreeNode | undefined {
  return tree.find(node => node.id === id);
}